import { isAfter, startOfDay } from 'date-fns';
import { useMemo, useRef, useState } from 'react';
import { formatToUSFullDate } from 'utils/dateTimeUtils/dateFormatUtil';
import {
    isDateValid,
    isDateWithinInterval,
    parseDateString
} from 'utils/dateTimeUtils/dateValidationUtil';

import { useEvent } from '@american-autonomy/ag-ui';

import { COMPLETE_DATE_LENGTH, DATE_FORMAT } from './constants';
import type { Props } from './types';

const useDatePicker = ({
    inputRef,
    interval,
    isFutureAllowed = true,
    onChange,
    popoverRef,
    value
}: Props) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(value);
    const [inputValue, setInputValue] = useState<string | undefined>(
        value ? formatToUSFullDate(value) : undefined
    );

    const showPopover = () => {
        const element = popoverRef.current as HTMLElement & { showPopover?: VoidFunction };

        element?.showPopover?.();
    };

    const hidePopover = () => {
        const element = popoverRef.current as HTMLElement & { hidePopover?: VoidFunction };

        element?.hidePopover?.();
    };

    const handleClickOutside = (event: Event) => {
        const target = event.target as HTMLElement;
        const isInsideDropdown = popoverRef.current?.contains(target);
        const isInsideInput = containerRef.current?.contains(target);

        if (!isInsideDropdown && !isInsideInput) {
            hidePopover();
        }
    };

    useEvent({ event: 'mousedown', handler: handleClickOutside });

    const isSelectable = (date: Date) => {
        if (!isDateValid(date)) {
            return false;
        }

        // Compare whole days so that today is never rejected for being a few
        // hours "ahead" of the current instant.
        if (!isFutureAllowed && isAfter(startOfDay(date), startOfDay(new Date()))) {
            return false;
        }

        return interval ? isDateWithinInterval(date, interval) : true;
    };

    const clear = () => {
        setSelectedDate(undefined);
        setInputValue(undefined);

        if (inputRef.current) {
            inputRef.current.value = '';
        }

        onChange?.(undefined);
        hidePopover();
    };

    const commit = (date: Date) => {
        const formatted = formatToUSFullDate(date);

        setSelectedDate(date);
        setInputValue(formatted);

        if (inputRef.current) {
            inputRef.current.value = formatted;
        }

        onChange?.(date);
    };

    const handleSelect = (date?: Date) => {
        if (!date) {
            clear();

            return;
        }

        commit(date);
        hidePopover();
    };

    const handleTextChange = () => {
        const currentValue = inputRef.current?.value ?? '';

        setInputValue(currentValue || undefined);

        if (!currentValue) {
            setSelectedDate(undefined);
            onChange?.(undefined);

            return;
        }

        if (currentValue.length !== COMPLETE_DATE_LENGTH) {
            return;
        }

        const parsedDate = parseDateString(currentValue, DATE_FORMAT);

        if (!isSelectable(parsedDate)) {
            return;
        }

        commit(parsedDate);
        hidePopover();
    };

    const handleBlur = () => {
        const currentValue = inputRef.current?.value ?? '';

        if (currentValue.length !== 0 && currentValue.length !== COMPLETE_DATE_LENGTH) {
            return;
        }

        if (selectedDate && isDateValid(selectedDate)) {
            setInputValue(formatToUSFullDate(selectedDate));

            return;
        }

        // No committed date, so clear anything the model never accepted. Unlike
        // DateRangePicker, which keeps valid-but-out-of-interval text so a manual
        // range entry can be completed in two steps, a single date has no second
        // step — leaving the text would show a value onChange never emitted.
        const parsedDate = parseDateString(currentValue, DATE_FORMAT);

        if (!isSelectable(parsedDate)) {
            setInputValue(undefined);
        }
    };

    const handleOpen = () => {
        inputRef.current?.focus();
        showPopover();
    };

    const disabledDays = useMemo(
        () => (isFutureAllowed ? undefined : { after: startOfDay(new Date()) }),
        [isFutureAllowed]
    );

    const endMonth = useMemo(() => {
        const intervalEnd = interval?.end as Date | undefined;

        if (isFutureAllowed) {
            return intervalEnd;
        }

        const today = new Date();

        if (!intervalEnd) {
            return today;
        }

        return intervalEnd < today ? intervalEnd : today;
    }, [interval, isFutureAllowed]);

    const dayPickerInterval = useMemo(() => {
        if (!interval) {
            return undefined;
        }

        return {
            after: interval.end as Date,
            before: interval.start as Date
        };
    }, [interval]);

    return {
        clear,
        containerRef,
        dayPickerInterval,
        disabledDays,
        endMonth,
        handleBlur,
        handleOpen,
        handleSelect,
        handleTextChange,
        inputValue,
        selectedDate
    };
};

export default useDatePicker;
